import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { BodyXXS } from '../../styles/Typography';

/** A consent shown as a short label, with the full legal authorization behind a toggle. */
export type AuthorizationConfig = {
  label: string;
  showFullTitle: string;
  hideFullTitle: string;
  /** Full legal authorization, one paragraph per entry. */
  fullText: string[];
};

type Props = {
  config: AuthorizationConfig;
  /** Prefix for the toggle's and the full text's test ids. */
  testId: string;
};

/** The "Show full authorization" toggle and text, rendered under a consent checkbox's label. */
export const FullAuthorization: FunctionComponent<Props> = ({ config, testId }) => {
  const [showFull, setShowFull] = useState(false);

  return (
    <>
      <ExpanderButton
        type="button"
        aria-expanded={showFull}
        data-testid={`${testId}-toggle`}
        onClick={() => setShowFull(!showFull)}
      >
        {showFull ? config.hideFullTitle : config.showFullTitle}
        <Chevron aria-hidden>{showFull ? '▴' : '▾'}</Chevron>
      </ExpanderButton>
      {showFull && (
        <FullText data-testid={`${testId}-full`}>
          {config.fullText.map((paragraph, index) => (
            <BodyXXS key={index} style={{ textAlign: 'left' }}>
              {paragraph}
            </BodyXXS>
          ))}
        </FullText>
      )}
    </>
  );
};

const ExpanderButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: ${({ theme }) => `${theme.spacing._8px} 0 0 ${theme.spacing._8px}`};
  color: ${Color.content.default};
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: underline;
  cursor: pointer;
`;

const Chevron = styled.span`
  margin-left: ${({ theme }) => theme.spacing._4px};
  font-size: ${({ theme }) => theme.fontSize.xxs};
`;

const FullText = styled.div`
  margin-top: ${({ theme }) => theme.spacing._8px};
  padding-left: ${({ theme }) => theme.spacing._8px};
  border-left: 2px solid ${Color.surface.muted};
`;
